"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useSession } from "@/lib/auth-react";
import { toast } from "@/lib/notify";
import {
  FaSliders,
  FaChevronLeft,
  FaChevronRight,
  FaXmark,
  FaFilter,
} from "react-icons/fa6";
import { Loading } from "@/components/UIs/Loading";
import { HeartIcon as OutlineHeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as SolidHeartIcon } from "@heroicons/react/24/solid";
import { IoIosArrowDown, IoIosSearch } from "react-icons/io";
import { toReadableFormat } from "@/utils/readableFormat";
import { HiMiniCalendarDays } from "react-icons/hi2";
import { GoXCircleFill } from "react-icons/go";
import { addToFavorites, removeFromFavorites } from "@/utils/favoriteUtils";
import { authFetch } from "@/utils/auth-fetch";
import Link from "next/link";
import { countryNames } from "@/utils/countryUtils";
import {
  Teacher,
  Focus,
  TeacherLevel,
  SpokenLanguage,
} from "@/types/teacherTypes";

const ITEMS_PER_PAGE = 5;

interface TeacherDirectoryProps {
  initialTeachers: Teacher[];
  focuses: Focus[];
  teacherLevels: TeacherLevel[];
  spokenLanguages: SpokenLanguage[];
}

export default function TeacherDirectory({
  initialTeachers,
  focuses,
  teacherLevels,
  spokenLanguages,
}: TeacherDirectoryProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const teachers = initialTeachers;
  const [loading, setLoading] = useState(false); // Initial loading is false as data is passed
  const [expandedBriefs, setExpandedBriefs] = useState<Record<string, boolean>>(
    {},
  );
  const [currentPage, setCurrentPage] = useState(1);
  const teacherSectionRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollSpeed = 2;
  const scrollInterval = 20;
  const [filters, setFilters] = useState<{
    level: boolean;
    duration: boolean;
    availability: boolean;
    country: boolean;
    focus: boolean;
    speaks: boolean;
    sort: boolean;
  }>({
    level: false,
    duration: false,
    availability: false,
    country: false,
    focus: false,
    speaks: false,
    sort: false,
  });
  const [favoriteStatuses, setFavoriteStatuses] = useState<
    Record<string, boolean>
  >({});
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);

  // For Filters
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedFocuses, setSelectedFocuses] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedSortOptions, setSelectedSortOptions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (session?.user?.role === "TEACHER") {
      router.push("/dashboard");
    }
  }, [session?.user?.role, router]);

  // Handle Filter Selection
  const toggleSelection = (filterType: string, value: string | number) => {
    setCurrentPage(1);

    switch (filterType) {
      case "country":
        setSelectedCountries((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      case "focus":
        setSelectedFocuses((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      case "language":
        setSelectedLanguages((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      case "level":
        setSelectedLevels((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      case "duration":
        setSelectedDurations((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      case "day":
        setSelectedDays((prev) =>
          prev.includes(value as number)
            ? prev.filter((item) => item !== value)
            : [...prev, value as number],
        );
        break;
      case "time":
        setSelectedTimes((prev) =>
          prev.includes(value as string)
            ? prev.filter((item) => item !== value)
            : [...prev, value as string],
        );
        break;
      default:
        break;
    }
  };

  // Filtering Logic
  const filteredTeachers = teachers.filter((teacher) => {
    const matchesCountry =
      selectedCountries.length === 0 ||
      selectedCountries.includes(teacher.country);

    const matchesFocus =
      selectedFocuses.length === 0 ||
      teacher.focuses?.some((focus) =>
        selectedFocuses.includes(toReadableFormat(focus.name)),
      );

    const matchesLanguages =
      selectedLanguages.length === 0 ||
      teacher.spokenLanguages?.some((language) =>
        selectedLanguages.includes(toReadableFormat(language.language)),
      );

    const matchesLevel =
      selectedLevels.length === 0 ||
      teacher.teacherLevels?.some((teacherLevel) =>
        selectedLevels.includes(toReadableFormat(teacherLevel.level)),
      );

    const matchesDuration =
      selectedDurations.length === 0 ||
      teacher.availabilities?.some((availability) => {
        const duration =
          parseInt(availability.endTime.split(":")[0], 10) * 60 +
          parseInt(availability.endTime.split(":")[1], 10) -
          (parseInt(availability.startTime.split(":")[0], 10) * 60 +
            parseInt(availability.startTime.split(":")[1], 10));
        return selectedDurations.includes(`${duration} min`);
      });

    const matchesDays =
      selectedDays.length === 0 ||
      teacher.availabilities?.some((availability) =>
        selectedDays.includes(availability.dayOfWeek),
      );

    const matchesTimes =
      selectedTimes.length === 0 ||
      teacher.availabilities?.some((availability) => {
        const startHour = parseInt(availability.startTime.split(":")[0], 10);
        const endHour = parseInt(availability.endTime.split(":")[0], 10);
        const isAM = startHour < 12 && endHour <= 12;
        const isPM = startHour >= 12 || endHour > 12;
        return (
          (selectedTimes.includes("AM") && isAM) ||
          (selectedTimes.includes("PM") && isPM)
        );
      });

    // Match the search query against the teacher's name AND the languages
    // they speak, so queries like "Bengali" surface teachers who list
    // Bengali as a spoken language. See issue #506.
    const queryLower = searchQuery.trim().toLowerCase();
    const matchesSearch =
      queryLower === "" ||
      teacher.name.toLowerCase().includes(queryLower) ||
      (teacher.spokenLanguages?.some((lang) =>
        lang.language.toLowerCase().includes(queryLower),
      ) ??
        false);

    return (
      matchesCountry &&
      matchesFocus &&
      matchesLanguages &&
      matchesLevel &&
      matchesDuration &&
      matchesDays &&
      matchesTimes &&
      matchesSearch
    );
  });

  const sortedTeachers = [...filteredTeachers].sort((a, b) => {
    if (selectedSortOptions?.includes("Highest"))
      return (b.rating || 0) - (a.rating || 0);
    if (selectedSortOptions?.includes("Lowest"))
      return (a.rating || 0) - (b.rating || 0);
    if (selectedSortOptions?.includes("Newest"))
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    return 0;
  });

  // Apply pagination AFTER sorting
  const displayedTeachers = sortedTeachers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // Total pages based on filtered teachers
  const totalPages = Math.ceil(filteredTeachers.length / ITEMS_PER_PAGE);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Clear Filters
  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedFocuses([]);
    setSelectedLanguages([]);
    setSelectedLevels([]);
    setSelectedDurations([]);
    setSelectedTimes([]);
    setSelectedDays([]);
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Fetch Favorite Statuses
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!session?.user?.id || !session?.accessToken || teachers.length === 0) return;

      try {
        const response = await authFetch("/api/favorites/check-multiple", session.accessToken!, {
          method: "POST",
          body: JSON.stringify({
            teacherIds: teachers.map((teacher) => teacher?.id),
          }),
        });

        const data = await response.json();
        const favorites = data.data?.favorites || [];
        const favMap = Array.isArray(favorites)
          ? favorites.reduce(
              (acc: Record<string, boolean>, fav: { teacherId: string; isFavorite: boolean }) => {
                acc[fav.teacherId] = fav.isFavorite;
                return acc;
              },
              {},
            )
          : {};
        setFavoriteStatuses(favMap);
      } catch (error) {
        console.error("Failed to fetch favorite statuses:", error);
      }
    };

    fetchFavorites();
  }, [session?.user?.id, session?.accessToken, teachers]);

  // Toogle favorite button
  const toggleFavorite = async (teacherId: string) => {
    if (!session?.user?.id || !session?.accessToken) {
      toast.error("You need to log in to favorite a teacher.");
      return;
    }

    try {
      const isFavorite = favoriteStatuses[teacherId];
      if (isFavorite) {
        await removeFromFavorites(teacherId, session.accessToken);
      } else {
        await addToFavorites(teacherId, session.accessToken);
      }

      // Update favorite statuses
      setFavoriteStatuses((prev) => ({
        ...prev,
        [teacherId]: !isFavorite,
      }));
    } catch (error) {
      console.error("Error toggling favorite status:", error);
      alert("An error occurred while updating favorite status.");
    }
  };

  // Toggle Filter
  const toggleFilter = (filter: keyof typeof filters) => {
    setFilters((prev) => {
      const newFilters = Object.keys(prev).reduce(
        (acc, key) => {
          acc[key as keyof typeof filters] =
            key === filter ? !prev[filter] : false;
          return acc;
        },
        {} as typeof filters,
      );
      return newFilters;
    });
  };

  // Handle click outside the filter
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterDropdowns = document.querySelectorAll(".filter-dropdown");
      let isClickInside = false;

      // Check if the click happened inside any dropdown
      filterDropdowns.forEach((dropdown) => {
        if (dropdown.contains(event.target as Node)) {
          isClickInside = true;
        }
      });

      // If the click happened outside, close all filters
      if (!isClickInside) {
        setFilters((prev) =>
          Object.keys(prev).reduce(
            (acc, key) => ({ ...acc, [key]: false }),
            {} as typeof filters,
          ),
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto Scroll Teachers
  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      // console.error("Scroll container not found!");
      return;
    }

    const scroll = () => {
      if (
        container.scrollLeft + container.offsetWidth >=
        container.scrollWidth
      ) {
        container.scrollLeft = 0; // Reset to the start
      } else {
        container.scrollLeft += scrollSpeed; // Move to the right
      }
    };

    const interval = setInterval(scroll, scrollInterval);

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [scrollSpeed, scrollInterval]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (filtersModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = ""; // Reset to default
    }

    // Cleanup on component unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [filtersModalOpen]);

  // Scroll behavior
  useEffect(() => {
    if (currentPage > 1) {
      teacherSectionRef.current?.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    }
  }, [currentPage]);


  const toggleBrief = (teacherId: string) => {
    setExpandedBriefs((prev) => ({
      ...prev,
      [teacherId]: !prev[teacherId], // Toggles expanded state per teacher
    }));
  };

  if (loading) {
    return (
      <div className="rounded-b-semifull bg-[#27335d] py-44 text-white">
        <Loading isOpen={true} />
      </div>
    );
  }

  return (
    <>
      {/* 2nd section - Teachers and filters */}
      <section ref={teacherSectionRef} className="bg-[#27335D]">
        <div className="rounded-b-semifull xsm:pb-5 bg-[#E9E9EA] pb-6 sm:pb-6 md:pb-16 lg:pb-20 xl:pb-24">
          <div className="rounded-b-semifull bg-[#ef7e74] py-0.5 md:py-5 lg:py-11 xl:py-12">
            {/* Filters */}
            <div className="mx-auto items-center justify-center space-y-8 md:mt-20">
              {/* First Row */}
              <div className="hidden flex-row items-center justify-center space-x-4 md:flex">
                {/* Choose Your Level */}
                <div className="relative max-w-fit flex-1">
                  {/* Badge for selected count */}
                  {selectedLevels.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                      {selectedLevels.length}
                    </span>
                  )}
                  <div
                    onClick={() => toggleFilter("level")}
                    data-testid="filterLevel"
                    className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                  >
                    Choose Your Level
                    <span
                      className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                        filters.level ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <IoIosArrowDown />
                    </span>
                  </div>
                  {filters.level && (
                    <div className="filter-dropdown absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                      {teacherLevels.map((level) => (
                        <div key={level.id}>
                          <div className="flex flex-row items-center gap-3.5 px-2 py-0.5 font-semibold text-[#27335D] md:px-2.5 md:py-1 md:text-base lg:px-3 lg:py-1 lg:text-lg xl:px-4 xl:py-2 xl:text-xl">
                            <input
                              type="checkbox"
                              id={`level-${level.id}`}
                              checked={selectedLevels.includes(
                                toReadableFormat(level.level),
                              )}
                              onChange={() =>
                                toggleSelection(
                                  "level",
                                  toReadableFormat(level.level),
                                )
                              }
                              className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                            />
                            <label
                              htmlFor={`level-${level.id}`}
                              className="cursor-pointer"
                            >
                              {toReadableFormat(level.level)}
                            </label>
                          </div>
                          <hr className="mx-auto border-t-[1.5px] border-[#27335D]" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>


                {/* I am Available */}
                <div className="relative max-w-fit flex-1">
                  {/* Badge for selected availability */}
                  {selectedDays.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                      {selectedDays.length}
                    </span>
                  )}
                  <div
                    onClick={() => toggleFilter("availability")}
                    data-testid="filterAvailability"
                    className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                  >
                    I am Available
                    <span
                      className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                        filters.availability ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <IoIosArrowDown />
                    </span>
                  </div>
                  {filters.availability && (
                    <div className="filter-dropdown absolute z-10 mt-2 w-full rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                      {[
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                      ].map((day, index) => (
                        <div key={day}>
                          <div className="flex flex-row items-center gap-1.5 px-2 py-0.5 font-semibold text-[#27335D] md:px-2.5 md:py-1 md:text-base lg:px-3 lg:py-1 lg:text-lg xl:px-4 xl:py-2 xl:text-xl">
                            <input
                              type="checkbox"
                              id={`availability-${day}`}
                              checked={selectedDays.includes(index)}
                              onChange={() => toggleSelection("day", index)}
                              className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                            />
                            <label
                              htmlFor={`availability-${day}`}
                              className="cursor-pointer"
                            >
                              {day}
                            </label>
                          </div>
                          {index < 6 && (
                            <hr className="mx-auto border-t-[1.5px] border-[#27335D]" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Country of Birth */}
                <div className="relative max-w-fit flex-1">
                  {/* Badge for selected count */}
                  {selectedCountries.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                      {selectedCountries.length}
                    </span>
                  )}
                  <div
                    onClick={() => toggleFilter("country")}
                    data-testid="filterCountry"
                    className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                  >
                    Country of Birth
                    <span
                      className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                        filters.country ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <IoIosArrowDown />
                    </span>
                  </div>
                  {filters.country && (
                    <div className="filter-dropdown scrollable-content-filter absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                      {Object.entries(countryNames)
                        .filter(([code]) => teachers.some((t) => t.country === code))
                        .map(([countryCode, countryName]) => (
                          <label
                            key={countryCode}
                            className="flex cursor-pointer items-center justify-start space-x-2 px-2 py-1 font-semibold text-[#27335D] md:px-2.5 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                          >
                            <div className="flex flex-1 items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`country-${countryCode}`}
                                checked={selectedCountries.includes(
                                  countryCode,
                                )}
                                onChange={() =>
                                  toggleSelection("country", countryCode)
                                }
                                className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                              />
                              <span className="truncate md:w-16 lg:w-[103px] xl:w-28">
                                {toReadableFormat(countryCode)}
                              </span>
                            </div>
                            <Image
                              src={`/images/flags/${countryCode || "WORLDWIDE"}.svg`}
                              alt={toReadableFormat(countryName)}
                              width={16}
                              height={12}
                              className="h-auto rounded-sm border-2 border-[#27335D] object-cover md:w-4 lg:w-5 xl:w-6"
                              onError={(e) => {
                                e.currentTarget.src = `/images/flags/WORLDWIDE.svg`;
                              }}
                            />
                          </label>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Second Row */}
              <div className="hidden flex-row items-center justify-center space-x-4 md:flex">
                {/* What's Your Focus */}
                <div className="relative max-w-fit flex-1">
                  {/* Badge for selected focuses */}
                  {selectedFocuses.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                      {selectedFocuses.length}
                    </span>
                  )}
                  <div
                    onClick={() => toggleFilter("focus")}
                    data-testid="filterFocus"
                    className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                  >
                    What&apos;s Your Focus
                    <span
                      className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                        filters.focus ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <IoIosArrowDown />
                    </span>
                  </div>
                  {filters.focus && (
                    <div className="filter-dropdown absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                      {focuses.filter((f) => teachers.some((t) => t.focuses?.some((tf) => tf.name === f.name))).map((focus) => (
                        <div key={focus.id}>
                          <div className="flex flex-row items-center gap-3.5 px-2 py-0.5 font-semibold text-[#27335D] md:px-2.5 md:py-1 md:text-base lg:px-3 lg:py-1 lg:text-lg xl:px-4 xl:py-2 xl:text-xl">
                            <input
                              type="checkbox"
                              id={`focus-${focus.id}`}
                              checked={selectedFocuses.includes(
                                toReadableFormat(focus.name),
                              )}
                              onChange={() =>
                                toggleSelection(
                                  "focus",
                                  toReadableFormat(focus.name),
                                )
                              }
                              className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                            />
                            <label
                              htmlFor={`focus-${focus.id}`}
                              className="cursor-pointer"
                            >
                              {toReadableFormat(focus.name)}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Also Speaks */}
                <div className="relative max-w-fit flex-1">
                  {/* Badge for selected languages */}
                  {selectedLanguages.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                      {selectedLanguages.length}
                    </span>
                  )}
                  <div
                    onClick={() => toggleFilter("speaks")}
                    data-testid="filterLanguage"
                    className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                  >
                    Also Speaks
                    <span
                      className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                        filters.speaks ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <IoIosArrowDown />
                    </span>
                  </div>
                  {filters.speaks && (
                    <div className="filter-dropdown scrollable-content-filter absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                      {spokenLanguages
                        .filter((lang) => teachers.some((t) => t.spokenLanguages?.some((sl) => sl.language === lang.language)))
                        .sort((a, b) => a.language.localeCompare(b.language))
                        .map((language) => (
                          <div key={language.id}>
                            <div className="flex flex-row items-center gap-2.5 px-2 py-0.5 font-semibold text-[#27335D] md:px-2.5 md:py-1 md:text-base lg:px-3 lg:py-1 lg:text-lg xl:px-4 xl:py-2 xl:text-xl">
                              <input
                                type="checkbox"
                                id={`speaks-${language.id}`}
                                checked={selectedLanguages.includes(
                                  toReadableFormat(language.language),
                                )}
                                onChange={() =>
                                  toggleSelection(
                                    "language",
                                    toReadableFormat(language.language),
                                  )
                                }
                                className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                              />
                              <label
                                htmlFor={`speaks-${language.id}`}
                                className="cursor-pointer truncate md:w-16 lg:w-[103px] xl:w-28"
                              >
                                {toReadableFormat(language.language)}
                              </label>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Search Bar For MD, LG, XL ... - Working */}
                <div className="relative md:w-90 lg:w-104 xl:w-[29.4rem]">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-4 flex items-center">
                      <IoIosSearch className="text-xl text-[#27335D]" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search for the perfect teacher"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="teacherDirectorySearchInput"
                      className="flex w-full items-center rounded-3xl border-2 border-[#27335D] bg-white px-12 py-3 text-start font-semibold text-[#27335D] focus:ring-2 focus:ring-[#4887F5] focus:outline-none md:py-1.5 md:text-base lg:py-2 lg:text-lg xl:py-3 xl:text-xl"
                    />
                    {/* Clear Search Button */}
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        data-testid="teacherDirectoryClearSearchButton"
                        className="absolute inset-y-0 right-4 flex items-center text-[#B53135] transition-all hover:text-[#D32F2F] focus:outline-none"
                      >
                        <GoXCircleFill className="text-xl" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Third Row */}
              <div className="hidden flex-row items-center justify-center space-x-4 md:flex">
                {/* Clear All Filters Button */}
                <div className="flex justify-center transition-all">
                  <button
                    data-testid="clearAllFiltersButton"
                    onClick={clearAllFilters}
                    disabled={
                      selectedLevels.length === 0 &&
                      selectedCountries.length === 0 &&
                      selectedFocuses.length === 0 &&
                      selectedLanguages.length === 0 &&
                      selectedDurations.length === 0 &&
                      selectedTimes.length === 0 &&
                      selectedDays.length === 0
                    }
                    className={`font-semibold transition-all ${
                      selectedFocuses.length === 0 &&
                      selectedLanguages.length === 0 &&
                      selectedLevels.length === 0 &&
                      selectedCountries.length === 0 &&
                      selectedFocuses.length === 0 &&
                      selectedLanguages.length === 0 &&
                      selectedDurations.length === 0 &&
                      selectedTimes.length === 0 &&
                      selectedDays.length === 0
                        ? "text-[#FCE8BD]"
                        : "text-stroke-navy dashed stroke-white text-[#F7C65A] underline underline-offset-2 hover:text-[#F9D17B]"
                    }`}
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
              {/* Items for xsm and sm */}
              <div className="flex flex-col">
                {/* Seach bar for XSM, SM ... - Working */}
                <div className="flex flex-row items-center justify-center gap-2 px-3 md:hidden">
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-4 flex items-center">
                        <IoIosSearch className="text-xl text-[#27335D]" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search for the perfect teacher"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="teacherDirectoryMobileSearchInput"
                        className="flex w-full items-center rounded-xl border-2 border-[#27335D] bg-white px-12 py-1 text-start font-semibold text-[#27335D] placeholder:text-[0.7rem] focus:ring-2 focus:ring-[#4887F5] focus:outline-none"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          data-testid="teacherDirectoryMobileClearSearchButton"
                          className="absolute inset-y-0 right-4 flex items-center text-[#B53135] hover:text-[#D32F2F] focus:outline-none"
                        >
                          <GoXCircleFill className="text-lg" />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Filters Button for xsm and sm */}
                  <div className="relative">
                    <button
                      onClick={() => setFiltersModalOpen(true)}
                      className="rounded-lg border-2 border-[#27335D] bg-[#3B4C88] px-[8px] py-[7px] text-xs font-semibold text-white"
                    >
                      <FaFilter />
                    </button>
                    {(selectedCountries.length > 0 ||
                      selectedFocuses.length > 0 ||
                      selectedLanguages.length > 0 ||
                      selectedLevels.length > 0) && (
                      <span className="absolute -top-1.5 -right-1 h-3 w-3 rounded-full border border-white bg-[#F6C65A] shadow-lg"></span>
                    )}
                  </div>
                </div>

                {/* Fourth Row */}
                <div className="flex flex-row items-start justify-center gap-3 px-3.5 pt-5 pb-5 md:items-center md:justify-center md:gap-16 md:px-0 md:pt-0 md:pb-20 lg:gap-6">
                  {/* Text */}
                  <div>
                    <p data-testid="teacherCount" className="text-stroke-white xsm:text-base text-base text-[#27335D] sm:text-lg md:text-2xl lg:text-3xl xl:text-4xl">
                      Learn Arabic with{" "}
                      <span className="text-[#F9D17B]">+01</span> Amazing{" "}
                      <span className="block md:inline">Teachers!</span>
                    </p>
                  </div>

                </div>
              </div>
            </div>
            {/* Teachers Cards */}
            <div className="mx-auto px-6 sm:max-w-sm md:max-w-7xl lg:px-8">
              {filteredTeachers.length === 0 ? (
                <p className="text-center text-xl font-bold text-[#27335d]">
                  No Results.
                </p>
              ) : (
                <>
                  {/* Teachers Section MD, LG, XL */}
                  {/* Container for Teacher Cards */}
                  <div className="hidden md:block">
                    <div className="flex flex-col items-center justify-center gap-8">
                      {displayedTeachers.map((teacher) => (
                        <div
                          key={teacher?.id}
                          className="w-full max-w-4xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl"
                        >
                          {/* Container for Image and Info */}
                          <div className="flex flex-row items-start space-x-4 rounded-t-3xl border-2 border-b-0 border-[#27335d] bg-white p-4 lg:space-x-6 lg:p-6 xl:space-x-7 xl:p-8">
                            {/* 🔹 Image Fix - Fixed Width & Height */}
                            <div className="relative h-[120px] w-[180px] overflow-hidden rounded-xl border border-[#27335D] md:h-[140px] md:w-[220px] lg:h-[160px] lg:w-[260px] xl:h-[180px] xl:w-[300px]">
                              <Image
                                src={teacher?.image || "/images/avatar.png"}
                                alt={teacher?.name}
                                layout="fill"
                                objectFit="cover" // Ensures the image maintains aspect ratio
                                className="transition-transform duration-300"
                                quality={100}
                                onError={(e) =>
                                  (e.currentTarget.src = "/images/avatar.png")
                                }
                              />
                            </div>

                            {/* 🔹 Teacher Info - Name, Reviews, Heart */}
                            <div className="flex flex-1 flex-col space-y-3">
                              {/* 🔸 Name & Rating */}
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                {/* Teacher Name */}
                                <Link href={`/find-a-teacher/${teacher?.profileLink}`} data-testid="teacherCardName" className="line-clamp-2 text-lg font-bold text-[#27335d] lg:text-xl xl:text-2xl hover:underline">
                                  {teacher?.name}
                                </Link>

                                {/* 🔸 Rating & Heart Icon */}
                                <div className="flex shrink-0 items-center space-x-3">
                                  <div className="flex flex-row items-center gap-1">
                                    <Image
                                      src="/images/svg/star.svg"
                                      alt="Star"
                                      width={14}
                                      height={14}
                                      className="h-4 w-4 object-contain"
                                    />
                                    <span className="text-base font-semibold text-[#27335D]">
                                      {teacher.rating
                                        ? teacher.rating.toFixed(1)
                                        : "0.0"}
                                    </span>
                                    <span className="text-xs font-medium text-[#7D859E]">
                                      {teacher.reviewCount ?? 0}{" "}
                                      {teacher.reviewCount === 1
                                        ? "Review"
                                        : "Reviews"}
                                    </span>
                                  </div>

                                  {/* Heart Icon */}
                                  <div
                                    className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                                    onClick={() => toggleFavorite(teacher?.id)}
                                  >
                                    {favoriteStatuses[teacher?.id] ? (
                                      <SolidHeartIcon className="h-6 w-6 text-red-600" />
                                    ) : (
                                      <OutlineHeartIcon className="h-6 w-6 text-gray-600" />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* 🔹 Languages & Focuses */}
                              <div className="space-y-1">
                                <div className="flex flex-row gap-2 text-xs text-[#667085] lg:text-sm">
                                  <Image
                                    src="/images/svg/graduationcap.svg"
                                    alt="Graduation Cap"
                                    width={14}
                                    height={14}
                                    className="h-4 w-4 object-contain"
                                  />
                                  <p className="truncate font-semibold">
                                    {teacher?.spokenLanguages?.length
                                      ? teacher?.spokenLanguages
                                          .map((spokenLanguage) =>
                                            toReadableFormat(
                                              spokenLanguage.language,
                                            ),
                                          )
                                          .join(", ")
                                      : "Arabic, English"}
                                  </p>
                                </div>

                                <div className="flex flex-row gap-2 text-xs text-[#667085] lg:text-sm">
                                  <Image
                                    src="/images/svg/rosette.svg"
                                    alt="Rosette"
                                    width={14}
                                    height={14}
                                    className="h-4 w-4 object-contain"
                                  />
                                  <p data-testid="teacherCardFocuses" className="truncate font-semibold">
                                    {teacher?.focuses?.length
                                      ? teacher?.focuses
                                          .map((focus) =>
                                            toReadableFormat(focus.name),
                                          )
                                          .join(", ")
                                      : "Basics"}
                                  </p>
                                </div>
                              </div>

                              {/* 🔹 Fixed Width Brief Section */}
                              <p
                                className={`w-full max-w-[300px] text-xs font-bold text-[#27335d] transition-all lg:max-w-[400px] lg:text-sm xl:max-w-[450px] xl:text-base ${
                                  expandedBriefs[teacher?.id]
                                    ? "line-clamp-none"
                                    : "line-clamp-2"
                                }`}
                              >
                                {teacher?.brief || "No Brief"}
                              </p>

                              {/* Button — only show if brief is long enough to be clamped */}
                              {teacher?.brief && teacher.brief.length > 80 && (
                              <div className="flex justify-start">
                                <button
                                  onClick={() => toggleBrief(teacher?.id)}
                                  className="text-xs font-semibold text-[#4887F5] underline lg:text-sm xl:text-base"
                                >
                                  {expandedBriefs[teacher?.id]
                                    ? "Read Less"
                                    : "Read More"}
                                </button>
                              </div>
                              )}
                            </div>
                          </div>

                          {/* Actions Section */}
                          <div className="flex flex-col items-center rounded-b-3xl bg-[#27335d]">
                            <div className="w-full px-4 py-3 text-base font-semibold text-white lg:text-lg xl:text-xl">
                              <div className="flex flex-row items-center justify-between gap-4 lg:gap-6 xl:gap-10">
                                {/* Teacher Country */}
                                <div className="flex flex-row items-center gap-2">
                                  <div>
                                    <Image
                                      src={`/images/flags/${teacher?.country || "WORLDWIDE"}.svg`}
                                      alt={teacher?.country || "Worldwide"}
                                      width={24}
                                      height={24}
                                      quality={100}
                                      className="rounded-md border-[2.5px] border-white"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-sm whitespace-nowrap sm:text-base lg:text-lg">
                                      {toReadableFormat(
                                        teacher?.country ?? "Unknown Country",
                                      )}
                                    </p>
                                  </div>
                                </div>

                                {/* Teaching Levels */}
                                <div data-testid="teacherCardLevels" className="flex flex-wrap gap-2">
                                  {teacher?.teacherLevels?.length
                                    ? teacher?.teacherLevels
                                        .slice()
                                        .sort(
                                          (a, b) =>
                                            [
                                              "BEGINNER",
                                              "INTERMEDIATE",
                                              "ADVANCED",
                                              "PROFICIENT",
                                            ].indexOf(a.level) -
                                            [
                                              "BEGINNER",
                                              "INTERMEDIATE",
                                              "ADVANCED",
                                              "PROFICIENT",
                                            ].indexOf(b.level),
                                        )
                                        .map((level) => (
                                          <span
                                            key={level.level}
                                            className={`rounded-full border border-[#27335D] px-3 py-1 text-xs font-semibold text-[#27335D] ${
                                              {
                                                BEGINNER: "bg-[#FCE8BD]",
                                                INTERMEDIATE: "bg-[#F9D17B]",
                                                ADVANCED: "bg-[#F6C65A]",
                                                PROFICIENT: "bg-[#fabc2d]",
                                              }[level.level] || "bg-gray-400"
                                            }`}
                                          >
                                            {toReadableFormat(level.level)}
                                          </span>
                                        ))
                                    : "All Levels"}
                                </div>

                                {/* Book Appointment */}
                                <button
                                  className="border-2 border-[#F6C65A] bg-[#FCE8BD] px-3 py-1 text-xs font-medium text-[#27335D] hover:cursor-pointer md:rounded-lg lg:rounded-xl lg:px-4 lg:py-1.5 lg:text-sm xl:rounded-2xl xl:px-5 xl:py-2 xl:text-base"
                                  onClick={() =>
                                    router.push(
                                      `/find-a-teacher/${teacher?.profileLink}#book`,
                                    )
                                  }
                                >
                                  Book Appointment
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* View More */}
                          <div className="relative z-10 flex flex-col items-center">
                            <button
                              data-testid="teacherCardViewMore"
                              className="shadow-custom m-auto rounded-2xl rounded-t-none bg-[#75C9EB] px-3 pt-0.5 pb-0.5 text-xs font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#98d6f0] active:border-t-0 active:bg-[#f7c65a] lg:px-4 lg:pt-0.5 lg:pb-1 lg:text-sm xl:px-5 xl:text-base"
                              onClick={() =>
                                router.push(
                                  `/find-a-teacher/${teacher?.profileLink}`,
                                )
                              }
                            >
                              View More
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Teachers Section XSM, SM */}
                  <div className="block md:hidden">
                    <div className="flex flex-col items-center justify-center gap-6">
                      {displayedTeachers.map((teacher) => (
                        <div key={teacher?.id} className="w-full max-w-4xl">
                          {/* Container for Image and Info */}
                          <div className="flex flex-col items-start rounded-t-3xl border-2 border-b-0 border-[#27335d] bg-white p-4">
                            {/* Teacher Image */}
                            <div className="relative h-40 w-full overflow-hidden rounded-xl border border-[#27335D] sm:h-52">
                              <Image
                                src={teacher?.image || "/images/avatar.png"}
                                alt={teacher?.name}
                                layout="fill"
                                objectFit="cover"
                                className="transition-transform duration-300"
                                quality={100}
                                onError={(e) =>
                                  (e.currentTarget.src = "/images/avatar.png")
                                }
                              />

                              {/* Rating */}
                              <div className="absolute top-3 left-3 flex items-center gap-1 rounded-md bg-white px-2 py-1 shadow-md">
                                <Image
                                  src="/images/svg/star.svg"
                                  alt="Star"
                                  width={10}
                                  height={10}
                                  className="h-3 w-3 object-contain"
                                />
                                <span className="text-xs font-bold text-[#27335D]">
                                  {teacher.rating
                                    ? teacher.rating.toFixed(1)
                                    : "0.0"}
                                </span>
                              </div>

                              {/* Heart Icon */}
                              <div
                                className="absolute top-3 right-3 rounded-full bg-white p-1 shadow-md hover:cursor-pointer"
                                onClick={() => toggleFavorite(teacher?.id)}
                              >
                                {favoriteStatuses[teacher?.id] ? (
                                  <SolidHeartIcon className="h-5 w-5 text-red-600" />
                                ) : (
                                  <OutlineHeartIcon className="h-5 w-5 text-gray-600" />
                                )}
                              </div>
                            </div>

                            {/* Teacher Info */}
                            <div className="mt-3 flex w-full flex-col space-y-2">
                              {/* Name */}
                              <div className="flex items-center justify-between space-x-2">
                                <Link href={`/find-a-teacher/${teacher?.profileLink}`} data-testid="teacherCardName" className="text-base font-semibold text-[#27335d] hover:underline">
                                  {teacher?.name}
                                </Link>
                              </div>
                              {/* Languages */}
                              <div className="flex flex-row gap-1 text-xs text-[#667085]">
                                <Image
                                  src="/images/svg/graduationcap.svg"
                                  alt="Graduation Cap"
                                  width={10}
                                  height={10}
                                  className="h-3 w-3 object-contain"
                                />
                                <p className="truncate font-semibold">
                                  {teacher?.spokenLanguages?.length
                                    ? teacher?.spokenLanguages
                                        .map((spokenLanguage) =>
                                          toReadableFormat(
                                            spokenLanguage.language,
                                          ),
                                        )
                                        .join(", ")
                                    : "Arabic, English"}
                                </p>
                              </div>
                              {/* Focuses */}
                              <div className="flex flex-row gap-1 text-xs text-[#667085]">
                                <Image
                                  src="/images/svg/rosette.svg"
                                  alt="Rosette"
                                  width={10}
                                  height={10}
                                  className="h-3 w-3 object-contain"
                                />
                                <p data-testid="teacherCardFocuses" className="truncate font-semibold">
                                  {teacher?.focuses?.length
                                    ? teacher?.focuses
                                        .map((focus) =>
                                          toReadableFormat(focus.name),
                                        )
                                        .join(", ")
                                    : ""}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Actions Section */}
                          <div className="flex flex-col items-start rounded-b-3xl bg-[#27335d] px-4 py-3 pe-5 text-white hover:cursor-pointer">
                            {/* Teacher Country */}
                            <div className="flex flex-row items-center gap-2 font-semibold text-white">
                              <div>
                                <Image
                                  src={`/images/flags/${teacher?.country || "WORLDWIDE"}.svg`}
                                  alt={teacher?.country || "Worldwide"}
                                  width={24}
                                  height={24}
                                  quality={100}
                                  className="rounded-md border-[2.5px] border-white"
                                />
                              </div>
                              <div>
                                <p className="text-sm whitespace-nowrap sm:text-base lg:text-lg">
                                  {toReadableFormat(
                                    teacher?.country ?? "Unknown Country",
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* View More */}
                          <div className="relative flex flex-col items-center">
                            <button
                              data-testid="teacherCardViewMore"
                              className="shadow-custom m-auto rounded-3xl rounded-t-none border-2 border-[#4887F5] bg-[#75C9EB] px-5 py-1 text-base font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#98d6f0] active:bg-[#f7c65a]"
                              onClick={() =>
                                router.push(
                                  `/find-a-teacher/${teacher?.profileLink}`,
                                )
                              }
                            >
                              View More
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Pagination — hide when only 1 page */}
            {totalPages > 1 && (
            <div className="mx-auto mt-10 mb-20 flex w-fit flex-row items-center justify-between gap-2.5 rounded-full border-2 border-[#52BBE6] bg-white px-2 py-1 md:mb-4 md:gap-4">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className={`font-semibold text-[#52BBE6] ${
                  currentPage === 1 ? "cursor-default opacity-50" : ""
                }`}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </button>

              {/* Page Numbers */}
              {(() => {
                const pageNumbers = [];
                const maxVisiblePages = 4; // Default for larger screens
                const smallScreenMaxPages = 1; // Adjust for small screens
                const isSmallScreen =
                  typeof window !== "undefined" && window.innerWidth < 768; // Detect small screens dynamically
                const visiblePages = isSmallScreen
                  ? smallScreenMaxPages
                  : maxVisiblePages;

                const hasLeftEllipsis = currentPage > visiblePages;
                const hasRightEllipsis =
                  currentPage < totalPages - visiblePages;

                // Always show the first page
                pageNumbers.push(
                  <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className={`rounded-full px-3 py-1 font-semibold transition ${
                      currentPage === 1
                        ? "bg-[#52BBE6] text-white"
                        : "text-[#52BBE6]"
                    }`}
                  >
                    1
                  </button>,
                );

                // Add left ellipsis if needed
                if (hasLeftEllipsis) {
                  pageNumbers.push(
                    <span
                      key="left-ellipsis"
                      className="cursor-default text-[#52BBE6]"
                    >
                      ...
                    </span>,
                  );
                }

                // Add middle pages
                const startPage = Math.max(
                  2,
                  currentPage - Math.floor(visiblePages / 2),
                );
                const endPage = Math.min(
                  totalPages - 1,
                  currentPage + Math.floor(visiblePages / 2),
                );

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      className={`rounded-full px-3 py-1 font-semibold transition ${
                        currentPage === i
                          ? "bg-[#52BBE6] text-white"
                          : "text-[#52BBE6]"
                      }`}
                    >
                      {i}
                    </button>,
                  );
                }

                // Add right ellipsis if needed
                if (hasRightEllipsis) {
                  pageNumbers.push(
                    <span
                      key="right-ellipsis"
                      className="cursor-default text-[#52BBE6]"
                    >
                      ...
                    </span>,
                  );
                }

                // Always show the last page
                if (totalPages > 1) {
                  pageNumbers.push(
                    <button
                      key={totalPages}
                      onClick={() => handlePageChange(totalPages)}
                      className={`rounded-full px-3 py-1 font-semibold transition ${
                        currentPage === totalPages
                          ? "bg-[#52BBE6] text-white"
                          : "text-[#52BBE6]"
                      }`}
                    >
                      {totalPages}
                    </button>,
                  );
                }

                return pageNumbers;
              })()}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className={`font-semibold text-[#52BBE6] ${
                  currentPage === totalPages ? "cursor-default opacity-50" : ""
                }`}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* 3rd section - Our Students Favotire */}
      <section className="bg-[#4887F5]">
        <div className="rounded-b-semifull xsm:pb-5 bg-[#E9E9EA] pb-6 sm:pb-6 md:pb-16 lg:pb-20 xl:pb-24">
          <div className="rounded-b-semifull bg-[#27335D] py-10 md:py-5 lg:py-11 xl:py-12">
            <div className="xsm:px-5 mx-auto mb-3 flex flex-row items-center justify-start px-5 sm:px-10 md:mt-10 md:px-20 lg:px-24 xl:px-28">
              <div className="text-start">
                <h1 className="xsm:text-xl text-xl font-bold tracking-tight text-white sm:text-2xl md:text-5xl lg:text-6xl xl:text-7xl">
                  Our Students Favorite
                </h1>
              </div>
            </div>
            <div className="relative">
              <div
                ref={scrollContainerRef}
                className="scrollable-content-v mx-auto flex max-w-[80%] gap-6 p-4 sm:max-w-[85%] md:max-w-[80%]"
              >
                {teachers.map((teacher) => (
                  <div
                    key={teacher.id}
                    className="w-68 flex-none shrink-0 rounded-lg"
                  >
                    {/* Teacher Card */}
                    <div className="flex flex-col items-start rounded-t-3xl border-2 border-b-0 border-[#4887F5] bg-white p-4">
                      {/* Teacher Image */}
                      <div className="relative h-36 w-full overflow-hidden rounded-xl border border-[#27335D]">
                        <Image
                          src={teacher.image || "/images/avatar.png"}
                          alt={teacher.name}
                          layout="fill"
                          objectFit="cover"
                          className="transition-transform duration-300"
                          quality={100}
                          onError={(e) =>
                            (e.currentTarget.src = "/images/avatar.png")
                          }
                        />

                        {/* Rating */}
                        <div className="absolute top-3 left-3 flex items-center gap-1 rounded-md bg-white px-2 py-1 shadow-md">
                          <Image
                            src="/images/svg/star.svg"
                            alt="Star"
                            width={10}
                            height={10}
                            className="h-3 w-3 object-contain"
                          />
                          <span className="text-xs font-bold text-[#27335D]">
                            {teacher.rating
                              ? teacher.rating.toFixed(1)
                              : "0.0"}
                          </span>
                        </div>

                        {/* Favorite Icon */}
                        <div
                          className="absolute top-3 right-3 rounded-full bg-white p-1 shadow-md hover:cursor-pointer"
                          onClick={() => toggleFavorite(teacher.id)}
                        >
                          {favoriteStatuses[teacher.id] ? (
                            <SolidHeartIcon className="h-5 w-5 text-red-600" />
                          ) : (
                            <OutlineHeartIcon className="h-5 w-5 text-gray-600" />
                          )}
                        </div>
                      </div>

                      {/* Teacher Info */}
                      <div className="mt-3 flex w-full flex-col space-y-2">
                        {/* Name */}
                        <Link href={`/find-a-teacher/${teacher.profileLink}`} data-testid="teacherCardName" className="text-base font-semibold text-[#27335D] hover:underline">
                          {teacher.name}
                        </Link>

                        {/* Languages */}
                        <div className="flex flex-row gap-1 text-xs text-[#667085]">
                          <Image
                            src="/images/svg/graduationcap.svg"
                            alt="Languages"
                            width={10}
                            height={10}
                            className="h-3 w-3 object-contain"
                          />
                          <p className="truncate font-semibold">
                            {teacher?.spokenLanguages?.length
                              ? teacher?.spokenLanguages
                                  .map((spokenLanguage) =>
                                    toReadableFormat(spokenLanguage.language),
                                  )
                                  .join(", ")
                              : "Arabic, English"}
                          </p>
                        </div>

                        {/* Focus */}
                        <div className="flex flex-row gap-1 text-xs text-[#667085]">
                          <Image
                            src="/images/svg/rosette.svg"
                            alt="Focus"
                            width={10}
                            height={10}
                            className="h-3 w-3 object-contain"
                          />
                          <p data-testid="teacherCardFocuses" className="truncate font-semibold">
                            {teacher?.focuses?.length
                              ? teacher?.focuses
                                  .map((focus) => toReadableFormat(focus.name))
                                  .join(", ")
                              : "Basics"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Section */}
                    <div className="flex flex-col items-end rounded-b-2xl bg-[#4887F5] pe-5">
                      <button className="mt-3 mb-2 rounded-lg border-2 border-[#F6C65A] bg-[#FCE8BD] p-1.5 hover:cursor-pointer hover:bg-[#f8dca7]">
                        <HiMiniCalendarDays className="text-base text-[#27335D]" />
                      </button>
                    </div>

                    {/* View More Button */}
                    <div className="relative flex flex-col items-center">
                      <button
                        data-testid="teacherCardViewMore"
                        className="shadow-custom m-auto rounded-3xl rounded-t-none border-2 border-[#4887F5] bg-[#FDF4DE] px-6 pt-0.5 pb-1 text-base font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#98d6f0] active:bg-[#f7c65a]"
                        onClick={() =>
                          router.push(`/find-a-teacher/${teacher.profileLink}`)
                        }
                      >
                        View More
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Modal for XSM And SM */}
      {filtersModalOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-[#3B4C88] text-white md:hidden">
          {/* Navigation Bar */}
          <div className="flex items-center justify-between bg-[#27335D] px-4 py-3">
            <button
              onClick={() => setFiltersModalOpen(false)}
              className="flex items-center gap-1 rounded-lg bg-[#B53135] p-2.5 font-semibold text-white"
            >
              <span className="text-lg">
                <FaXmark />
              </span>
              <span>Close</span>
            </button>
            <button
              onClick={clearAllFilters} // Clear all selected filters
              disabled={
                selectedLevels.length === 0 &&
                selectedCountries.length === 0 &&
                selectedFocuses.length === 0 &&
                selectedLanguages.length === 0 &&
                selectedDurations.length === 0 &&
                selectedTimes.length === 0 &&
                selectedDays.length === 0
              }
              className={`rounded-lg px-4 py-2 font-semibold transition-all ${
                selectedFocuses.length === 0 &&
                selectedLanguages.length === 0 &&
                selectedLevels.length === 0 &&
                selectedCountries.length === 0 &&
                selectedFocuses.length === 0 &&
                selectedLanguages.length === 0 &&
                selectedDurations.length === 0 &&
                selectedTimes.length === 0 &&
                selectedDays.length === 0
                  ? "text-gray-500"
                  : "text-[#4887F5]"
              }`}
            >
              Remove
            </button>
          </div>
          {/* Modal Content */}
          <div className="scrollable-content-sm my-5 flex-1 overflow-auto p-6">
            <div className="mx-auto max-w-md rounded-xl bg-white p-6">
              <div className="space-y-4">
                {/* Choose Your Level */}
                <div className="relative">
                  <details className="group font-semibold text-[#27335D]">
                    <summary className="flex cursor-pointer items-center justify-between px-4 py-2 text-lg font-semibold">
                      Choose Your Level
                      <span className="text-[#AD9F7F] transition-transform duration-200 group-open:rotate-180">
                        <IoIosArrowDown />
                      </span>
                    </summary>
                    <div className="space-y-2 px-4 py-2">
                      {teacherLevels.map((level) => (
                        <label
                          key={level.level}
                          className="flex cursor-pointer items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLevels.includes(
                              toReadableFormat(level.level),
                            )}
                            onChange={() =>
                              toggleSelection(
                                "level",
                                toReadableFormat(level.level),
                              )
                            }
                            className="cursor-pointer form-checkbox h-4 w-4 appearance-none rounded border-2 border-[#F6C65A] checked:bg-[#F6C65A] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                          />
                          <span>{toReadableFormat(level.level)}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                  {selectedLevels.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                      {selectedLevels.length}
                    </span>
                  )}
                </div>
                <hr className="my-2 border-t-[1.5px] border-[#E5E7EB]" />


                {/* I am Available */}
                <div className="relative">
                  <details className="group font-semibold text-[#27335D]">
                    <summary className="flex cursor-pointer items-center justify-between px-4 py-2 text-lg font-semibold">
                      I am Available
                      <span className="text-[#AD9F7F] transition-transform duration-200 group-open:rotate-180">
                        <IoIosArrowDown />
                      </span>
                    </summary>
                    <div className="space-y-4 px-4 py-2">
                      {/* Days Options */}
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          "Sunday",
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                        ].map((day, index) => (
                          <label
                            key={day}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedDays.includes(index)}
                              onChange={() => toggleSelection("day", index)}
                              className="cursor-pointer form-checkbox h-4 w-4 appearance-none rounded border-2 border-[#F6C65A] checked:bg-[#F6C65A] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </details>
                  {selectedDays.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                      {selectedDays.length}
                    </span>
                  )}
                </div>
                <hr className="my-2 border-t-[1.5px] border-[#E5E7EB]" />

                {/* Country of Birth */}
                <div className="relative">
                  <details className="group font-semibold text-[#27335D]">
                    <summary className="flex cursor-pointer items-center justify-between px-4 py-2 text-lg font-semibold">
                      Country of Birth
                      <span className="text-[#AD9F7F] transition-transform duration-200 group-open:rotate-180">
                        <IoIosArrowDown />
                      </span>
                    </summary>
                    <div className="space-y-2 px-4 py-2">
                      {Object.keys(countryNames).map((countryCode) => (
                        <label
                          key={countryCode}
                          className="flex cursor-pointer items-center justify-start space-x-2"
                        >
                          <div className="flex flex-1 items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={selectedCountries.includes(countryCode)}
                              onChange={() =>
                                toggleSelection("country", countryCode)
                              }
                              className="cursor-pointer form-checkbox h-4 w-4 appearance-none rounded border-2 border-[#F6C65A] checked:bg-[#F6C65A] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                            />
                            <span className="xsm:w-32 w-32 truncate sm:w-48">
                              {toReadableFormat(countryCode)}
                            </span>
                          </div>
                          <Image
                            src={`/images/flags/${countryCode || "WORLDWIDE"}.svg`}
                            alt={toReadableFormat(countryCode)}
                            width={16}
                            height={12}
                            className="h-auto w-5 rounded-sm border border-[#27335D] object-cover"
                            onError={(e) => {
                              e.currentTarget.src = `/images/flags/WORLDWIDE.svg`;
                            }}
                          />
                        </label>
                      ))}
                    </div>
                  </details>
                  {selectedCountries.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                      {selectedCountries.length}
                    </span>
                  )}
                </div>
                <hr className="my-2 border-t-[1.5px] border-[#E5E7EB]" />

                {/* What’s Your Focus */}
                <div className="relative">
                  <details className="group font-semibold text-[#27335D]">
                    <summary className="flex cursor-pointer items-center justify-between px-4 py-2 text-lg font-semibold">
                      What’s Your Focus
                      <span className="text-[#AD9F7F] transition-transform duration-200 group-open:rotate-180">
                        <IoIosArrowDown />
                      </span>
                    </summary>
                    <div className="space-y-2 px-4 py-2">
                      {focuses.filter((f) => teachers.some((t) => t.focuses?.some((tf) => tf.name === f.name))).map((focus) => (
                        <label
                          key={focus.name}
                          className="flex cursor-pointer items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFocuses.includes(
                              toReadableFormat(focus.name),
                            )}
                            onChange={() =>
                              toggleSelection(
                                "focus",
                                toReadableFormat(focus.name),
                              )
                            }
                            className="cursor-pointer form-checkbox h-4 w-4 appearance-none rounded border-2 border-[#F6C65A] checked:bg-[#F6C65A] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                          />
                          <span>{toReadableFormat(focus.name)}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                  {selectedFocuses.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                      {selectedFocuses.length}
                    </span>
                  )}
                </div>
                <hr className="my-2 border-t-[1.5px] border-[#E5E7EB]" />

                {/* Also Speaks */}
                <div className="relative">
                  <details className="group font-semibold text-[#27335D]">
                    <summary className="flex cursor-pointer items-center justify-between px-4 py-2 text-lg font-semibold">
                      Also Speaks
                      <span className="text-[#AD9F7F] transition-transform duration-200 group-open:rotate-180">
                        <IoIosArrowDown />
                      </span>
                    </summary>
                    <div className="space-y-2 px-4 py-2">
                      {spokenLanguages.map((lang) => (
                        <label
                          key={lang.language}
                          className="flex cursor-pointer items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLanguages.includes(
                              toReadableFormat(lang.language),
                            )}
                            onChange={() =>
                              toggleSelection(
                                "language",
                                toReadableFormat(lang.language),
                              )
                            }
                            className="cursor-pointer form-checkbox h-4 w-4 appearance-none rounded border-2 border-[#F6C65A] checked:bg-[#F6C65A] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                          />
                          <span>{toReadableFormat(lang.language)}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                  {selectedLanguages.length > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                      {selectedLanguages.length}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

